import type { IPriceImprovementBlotterRequest } from 'apiConnector/blotters/priceImprovementBlotter';
import type { PriceImprovementsAcknowledgeRequest } from 'apiConnector/price-improvement';
import type { IBlotterPreferenceState } from 'components/shared/Blotter/Grid/blotter.model';
import { createSelector } from 'reselect';
import { getIsTraderUser } from 'state/selectors';
import type { AppState } from 'state/state.model';
import { getUserPreferencesLanguage } from 'state/userPreferences/userPreferences.selectors';
import { getTodayStringInLocalTimezone, toEndOfDay, toStartOfDay } from 'utils/core/date';
import { now } from 'utils/core/now';
import { translateText } from 'utils/lang/lang';

import type { IBlotterColumnDefinition, Sort } from '../common/blotter.model';
import { toLocaleDate, toLocaleDateOnlyString } from '../common/blotter.utils';
import type {
  IPriceImprovementBlotterFilters,
  IPriceImprovementBlotterFiltersWithMandatoryDates,
  IPriceImprovementBlotterItem,
  PriceImprovementBlotterState,
} from './priceImprovementBlotter.model';

function getPriceImprovementBlotterState(state: AppState) {
  return state.blotters.priceImprovement;
}

export function getPreferenceState(appState: AppState): IBlotterPreferenceState {
  const state = getPriceImprovementBlotterState(appState);
  if (state.blotterPreferenceState && !state.blotterPreferenceState.pageSizeState) {
    state.blotterPreferenceState.pageSizeState = 25;
  }
  return state.blotterPreferenceState;
}

export function getPaginationInfo(appState: AppState) {
  const state = getPriceImprovementBlotterState(appState);
  return {
    page: state.page,
    itemsPerPage: state.blotterPreferenceState.pageSizeState,
    totalItems: state.totalItems,
    totalPages: state.totalPages,
  };
}

const emptyArray: readonly [] = [];

export function getPageItems(appState: AppState) {
  const state = getPriceImprovementBlotterState(appState);
  return state.items ?? emptyArray;
}

export function getColumnDefinition(
  appState: AppState,
): readonly IBlotterColumnDefinition<IPriceImprovementBlotterItem>[] {
  const state = getPriceImprovementBlotterState(appState);
  return state.columnDefinition;
}

export function getBlotterPreferenceState(appState: AppState): IBlotterPreferenceState {
  const state = getPriceImprovementBlotterState(appState);
  if (state.blotterPreferenceState && !state.blotterPreferenceState.pageSizeState) {
    state.blotterPreferenceState.pageSizeState = 25;
  }
  return state.blotterPreferenceState;
}

export function isEndDateInThePast(appState: AppState): boolean {
  const state = getPriceImprovementBlotterState(appState);
  const { endDate } = getFilters(state);
  const today = getTodayStringInLocalTimezone();
  return new Date(today) > new Date(endDate);
}

const getSortColumns: (state: AppState) => Sort[] = createSelector(
  [(s: AppState) => s.blotters.priceImprovement.blotterPreferenceState.columnState],
  columns => {
    const result =
      columns
        ?.filter(c => c.sort === 'asc' || c.sort === 'desc')
        .map(({ colId, sort }) => ({ column: colId!, direction: sort! })) ?? [];
    return result;
  },
);

export const getFilters: (state: PriceImprovementBlotterState) => IPriceImprovementBlotterFiltersWithMandatoryDates =
  createSelector(
    [
      (s: PriceImprovementBlotterState) => s.quickSearchTerm,
      (s: PriceImprovementBlotterState) => s.advancedFilters,
      (s: PriceImprovementBlotterState) => s.startDate,
      (s: PriceImprovementBlotterState) => s.endDate,
      (s: PriceImprovementBlotterState) => s.dateFilter,
      (s: PriceImprovementBlotterState) => s.activityFilter,
      (s: PriceImprovementBlotterState) => s.statusFilter,
    ],
    (quickSearchTerm, advancedFilters, startDate, endDate, dateFilter, activityFilter, statusFilter) => {
      const custom = 'custom';
      return {
        quickSearchTerm,
        dateFilter: advancedFilters && startDate ? custom : dateFilter,
        activityFilter,
        advancedFilters,
        startDate: (advancedFilters && startDate) || toLocaleDateOnlyString(toLocaleDate(dateFilter)),
        endDate: (advancedFilters && endDate) || toLocaleDateOnlyString(now()),
        statusFilter,
      };
    },
  );

export function getFiltersToPersist(state: PriceImprovementBlotterState): IPriceImprovementBlotterFilters {
  return {
    quickSearchTerm: state.quickSearchTerm,
    dateFilter: state.dateFilter,
    activityFilter: state.activityFilter,
    statusFilter: state.statusFilter,
    advancedFilters: state.advancedFilters,
    startDate: state.startDate,
    endDate: state.endDate,
  };
}

export const getBlotterRequest: (state: AppState) => IPriceImprovementBlotterRequest = createSelector(
  [
    (s: AppState) => s.blotters.priceImprovement.page,
    (s: AppState) => s.blotters.priceImprovement.blotterPreferenceState.pageSizeState,
    (s: AppState) => getFilters(s.blotters.priceImprovement),
    getUserPreferencesLanguage,
    getSortColumns,
  ],
  (page, pageSize, { quickSearchTerm, startDate, endDate, activityFilter, statusFilter }, lang, sort) => {
    return {
      activityScope: activityFilter,
      endDate: toEndOfDay(new Date(endDate)).toISOString(),
      fullTextSearch: quickSearchTerm ? translateText(lang, 'en', quickSearchTerm) : undefined,
      page: page - 1,
      size: pageSize ?? 25,
      sort,
      startDate: toStartOfDay(new Date(startDate)).toISOString(),
      status: statusFilter,
    };
  },
);

export const canAcknowledge = createSelector(
  getSelectedRowIds,
  (state: AppState) => getPriceImprovementBlotterState(state).items,
  (state: AppState) => getIsTraderUser(state),
  (selectedRowIds, items, isTrader) =>
    isTrader === true &&
    selectedRowIds.length > 0 &&
    (items?.filter(item => selectedRowIds.includes(item.id)).every(item => item.status === 'Pending') || false),
);

export const getPendingItemsCount = createSelector(
  (state: AppState) => getPriceImprovementBlotterState(state).items,
  items => items?.filter(item => item.status === 'Pending').length || 0,
);

export function getSelectedRowIds(state: AppState) {
  return getStateSelectedRowIds(getPriceImprovementBlotterState(state));
}

function getStateSelectedRowIds(priceImprovementState: PriceImprovementBlotterState) {
  return priceImprovementState.selectedRowIds;
}
export function getAcknowledgementRequest(state: AppState): PriceImprovementsAcknowledgeRequest {
  const priceImprovementState = getPriceImprovementBlotterState(state);
  const rowIds = getStateSelectedRowIds(priceImprovementState);

  const improvements = rowIds.map(rowId => {
    return {
      requestForPriceImprovementId: rowId,
    };
  });
  return {
    improvements,
  };
}

export function getSelectionSize(state: AppState) {
  return getSelectedRowIds(state).length;
}

export function canDisplayDetail(state: AppState) {
  return !!getDetailItem(state);
}

export function getDetailItem(state: AppState) {
  const priceImprovementState = getPriceImprovementBlotterState(state);
  return priceImprovementState.detail?.item;
}

export function getDetailSensitivity(state: AppState) {
  const priceImprovementState = getPriceImprovementBlotterState(state);
  return priceImprovementState.detail?.sensitivity;
}

export function getPriceImprovementBlotterLoading(state: AppState) {
  return getPriceImprovementBlotterState(state).loading;
}

export function getPriceImprovementBlotterError(state: AppState) {
  return getPriceImprovementBlotterState(state).error;
}

export function getAcknowledgementError(state: AppState) {
  return getPriceImprovementBlotterState(state).acknowledgementError;
}

export function areBlotterPreferencesInError(state: AppState): boolean {
  return getPriceImprovementBlotterState(state).areBlotterPreferencesInError;
}

export function canSaveBlotterPreferences(state: AppState): boolean {
  return getPriceImprovementBlotterState(state).canSaveBlotterPreferences;
}
